import { BrandKit, ImageKind, MessageTemplatesService } from "client/openapi";
import { useAutosave } from "components/Forms/useAutosave";
import { COMPACT_INPUT } from "components/Inputs/compact";
import {
  EmailFrame,
  useTemplatePreview,
} from "components/MessageTemplates/TemplatePreviewParts";
import ImageUpload from "components/PublicBooking/ImageUpload";
import { OrgLogo } from "components/PublicBooking/PublicShell";
import { zoneLabel } from "components/PublicBooking/publicBooking";
import { Select } from "components/Select";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { openOrganizationSettings } from "util/organizationSettings";
import { LABEL, SECTION, SECTION_TEXT, SECTION_TITLE } from "./settingsStyles";

const SAVED_EVENT = "tadpole:email-brand-saved";

/** Its tab in Organization Settings. */
export const EMAIL_BRAND_TAB = "Email brand";

/** Opens Organization Settings on the email brand, from any page. */
export function openEmailBrandSettings() {
  openOrganizationSettings(EMAIL_BRAND_TAB);
}

/** Calls onSaved each time the email brand (or its logo) is saved. */
export function useEmailBrandSaved(onSaved: () => void) {
  const latest = useRef(onSaved);
  latest.current = onSaved;
  useEffect(() => {
    const listener = () => latest.current();
    window.addEventListener(SAVED_EVENT, listener);
    return () => window.removeEventListener(SAVED_EVENT, listener);
  }, []);
}

/** "8 AM", "noon", "8 PM", "midnight". */
function hourWords(hour: number) {
  if (hour === 0 || hour === 24) return "midnight";
  if (hour === 12) return "noon";
  return `${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`;
}

type Choice<T> = { value: T; label: string };
const fontChoice = (font: string): Choice<string> => ({
  value: font,
  label: font,
});
const hourChoice = (hour: number): Choice<number> => ({
  value: hour,
  label: hourWords(hour),
});

// A Select opens on its value only when the value is one of its options
// (the same object), so the choices are made once and the value is found
// among them.
const HOURS = Array.from({ length: 25 }, (_, hour) => hourChoice(hour));
const FROM_HOURS = HOURS.slice(0, 24);
const UNTIL_HOURS = HOURS.slice(1);

/** What the brand kit saves (the logo saves on its own). */
type BrandFields = Pick<
  BrandKit,
  | "primary_color"
  | "secondary_color"
  | "font"
  | "footer"
  | "send_from_hour"
  | "send_until_hour"
>;

const fieldsOf = (brand: BrandKit): BrandFields => ({
  primary_color: brand.primary_color,
  secondary_color: brand.secondary_color,
  font: brand.font,
  footer: brand.footer ?? "",
  send_from_hour: brand.send_from_hour,
  send_until_hour: brand.send_until_hour,
});

// The sample the preview shows, in the brand as saved.
const SAMPLE = {
  email_subject: "A note from {{org.name}}",
  email_body:
    "# Hi {{parent.first_name | there}},\n\nThis is how your template emails look: your logo, colors, and font, with your footer at the bottom.\n\n[[A button|https://example.com]]\n\nThank you,\n{{org.name}}",
  email_format: "editor" as const,
};

/**
 * Organization Settings > Email brand: the look of template emails (their
 * own logo and colors, apart from the booking pages', the font and the
 * footer) and the hours automatic messages go out in, with a sample email
 * in the brand. Everything saves as it changes, and only what changed (so
 * it never undoes another admin's change to the rest): the tab has no Save.
 * It loads the first time `wanted` is true, for the organization chosen,
 * and again when it is wanted after a load failed.
 */
export default function EmailBrandSettings({
  wanted = true,
}: {
  wanted?: boolean;
}) {
  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );
  const orgId = currently_selected_organization as number;
  const [brand, setBrand] = useState<BrandKit>();
  const [loadFailed, setLoadFailed] = useState(false);
  // Each save (and logo change) shows in the sample again, and in an open
  // template's preview.
  const [saves, setSaves] = useState(0);
  const saved = () => {
    setSaves((n) => n + 1);
    window.dispatchEvent(new Event(SAVED_EVENT));
  };
  // The fields as last saved: a save sends only those that differ.
  const stored = useRef<BrandFields>();

  const loaded = brand !== undefined;
  const fields = brand ? fieldsOf(brand) : undefined;
  const hoursWrong = !!brand && brand.send_from_hour >= brand.send_until_hour;
  const { pending, error, markSaved } = useAutosave(
    hoursWrong ? undefined : fields,
    (now) => {
      const changes = Object.fromEntries(
        Object.entries(now).filter(
          ([key, value]) => stored.current?.[key as keyof BrandFields] !== value
        )
      ) as Partial<BrandFields>;
      if (Object.keys(changes).length === 0) return Promise.resolve();
      return MessageTemplatesService.updateMessageTemplateBrand({
        orgId,
        requestBody: changes,
      }).then(() => {
        stored.current = { ...(stored.current as BrandFields), ...changes };
        saved();
      });
    },
    800
  );

  useEffect(() => {
    if (!wanted || loaded) return;
    let current = true;
    setLoadFailed(false);
    MessageTemplatesService.getMessageTemplateBrand({ orgId })
      .then((kit) => {
        if (!current) return;
        stored.current = fieldsOf(kit);
        markSaved(fieldsOf(kit));
        setBrand(kit);
      })
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
  }, [orgId, wanted, loaded, markSaved]);

  const set = (changes: Partial<BrandKit>) =>
    setBrand((current) => current && { ...current, ...changes });

  const fontOptions = useMemo(
    () => (brand?.fonts?.length ? brand.fonts : []).map(fontChoice),
    [brand?.fonts]
  );

  const { preview } = useTemplatePreview(
    orgId,
    loaded ? SAMPLE : null,
    0,
    saves
  );

  const status = error
    ? error
    : hoursWrong
    ? "Not saved: pick sending hours that start before they end."
    : pending
    ? "Saving…"
    : "Saved";

  if (loadFailed) {
    return (
      <section className={`${SECTION} max-w-3xl`}>
        <h3 className={SECTION_TITLE}>Email brand</h3>
        <p className="mt-2 text-sm text-red-600">
          Your email brand couldn&apos;t be loaded. Close Organization Settings
          and open it again.
        </p>
      </section>
    );
  }
  if (!brand) {
    return (
      <div className="h-72 max-w-3xl animate-pulse rounded-lg bg-gray-100" />
    );
  }

  return (
    // The settings, and the sample email in a column of its own beside them.
    <div className="grid max-w-6xl items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="flex flex-col gap-4">
        <section className={SECTION} aria-labelledby="email-brand-title">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 id="email-brand-title" className={SECTION_TITLE}>
              Email brand
            </h3>
            <span
              className={`text-xs ${
                error || hoursWrong ? "text-red-600" : "text-gray-500"
              }`}
              aria-live="polite"
            >
              {status}
            </span>
          </div>
          <p className={SECTION_TEXT}>
            Every message template email uses your logo, colors, and font.
            Changes save as you make them. They don&apos;t change your booking
            pages.
          </p>

          <div className="mt-4">
            <ImageUpload
              orgId={orgId}
              kind={ImageKind.EMAIL_LOGO}
              hasImage={!!brand.logo_url}
              noun="logo"
              label={<p className={`${LABEL} mb-1.5`}>Logo</p>}
              className="flex items-center gap-4"
              onUploaded={(url) => {
                set({ logo_url: url });
                saved();
              }}
              onRemove={() =>
                MessageTemplatesService.removeMessageTemplateBrandLogo({
                  orgId,
                }).then((kit) => {
                  set({ logo_url: kit.logo_url });
                  saved();
                })
              }
            >
              {brand.logo_url ? (
                <OrgLogo
                  url={brand.logo_url}
                  name="Your logo"
                  size={64}
                  maxWidth={160}
                  accent={brand.primary_color}
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-400">
                  No logo
                </div>
              )}
            </ImageUpload>

            <div className="mt-4 flex flex-wrap gap-6">
              {(
                [
                  [
                    "primary_color",
                    "Main color",
                    "Headings, links, and buttons.",
                  ],
                  ["secondary_color", "Accent color", "Dividers."],
                ] as const
              ).map(([key, label, note]) => (
                <div key={key}>
                  <label htmlFor={`brand-${key}`} className={LABEL}>
                    {label}
                  </label>
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <input
                      id={`brand-${key}`}
                      type="color"
                      value={brand[key]}
                      onChange={(e) =>
                        set({
                          [key]: e.target.value.toUpperCase(),
                        } as Partial<BrandKit>)
                      }
                      className="h-8 w-8 cursor-pointer rounded border-0 p-0"
                    />
                    {brand[key]}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{note}</p>
                </div>
              ))}
            </div>

            <label htmlFor="brand-font" className={`${LABEL} mt-4 block`}>
              Font
            </label>
            <div className="mt-1 w-56">
              <Select
                inputId="brand-font"
                isSearchable={false}
                menuPlacement="auto"
                options={fontOptions}
                value={
                  fontOptions.find((f) => f.value === brand.font) ??
                  fontChoice(brand.font)
                }
                onChange={(font: Choice<string>) => set({ font: font.value })}
                // Each font is shown in itself.
                formatOptionLabel={(font: Choice<string>) => (
                  <span style={{ fontFamily: font.value }}>{font.label}</span>
                )}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Mail apps without this font show a similar one.
            </p>

            <label htmlFor="brand-footer" className={`${LABEL} mt-4 block`}>
              Footer line (optional)
            </label>
            <input
              id="brand-footer"
              type="text"
              maxLength={500}
              value={brand.footer ?? ""}
              placeholder="Thank you for learning with us!"
              onChange={(e) => set({ footer: e.target.value })}
              className={`${COMPACT_INPUT} mt-1 w-full`}
            />
            <p className="mt-1 text-xs text-gray-500">
              Shown at the bottom of every email, above your name, phone, and
              email.
            </p>
          </div>
        </section>

        <section className={SECTION}>
          <h3 className={SECTION_TITLE}>Sending hours</h3>
          <p className={SECTION_TEXT}>
            Automatic messages from message templates only go out between these
            hours, {zoneLabel(brand.time_zone)}. Any that come up outside them
            wait until {hourWords(brand.send_from_hour)}.
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <label htmlFor="brand-from" className="sr-only">
              From
            </label>
            <div className="w-32">
              <Select
                inputId="brand-from"
                isSearchable={false}
                menuPlacement="auto"
                options={FROM_HOURS}
                value={FROM_HOURS.find((h) => h.value === brand.send_from_hour)}
                onChange={(hour: Choice<number>) =>
                  set({ send_from_hour: hour.value })
                }
              />
            </div>
            to
            <label htmlFor="brand-until" className="sr-only">
              Until
            </label>
            <div className="w-32">
              <Select
                inputId="brand-until"
                isSearchable={false}
                menuPlacement="auto"
                options={UNTIL_HOURS}
                value={UNTIL_HOURS.find(
                  (h) => h.value === brand.send_until_hour
                )}
                onChange={(hour: Choice<number>) =>
                  set({ send_until_hour: hour.value })
                }
              />
            </div>
          </div>
          {hoursWrong && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              Pick a start before the end.
            </p>
          )}
        </section>
      </div>

      <div className="lg:sticky lg:top-4">
        <p className={`${LABEL} mb-1.5`}>Sample email</p>
        {preview ? (
          <EmailFrame
            html={preview.html}
            title="A sample email in your brand"
            height={560}
          />
        ) : (
          <div className="h-[560px] animate-pulse rounded-lg bg-gray-100" />
        )}
      </div>
    </div>
  );
}
